import { useEffect, useState } from "react";
import { getWorkspaceProfile } from "../../api/agent";
import MemoryPanel from "./MemoryPanel";
import styles from "../../pages/Agent/AgentDashboard.module.css";
export function requestSites(request: string): string[] {
  const matches =
    request.match(
      /(?:https?:\/\/|www\.)[^\s<>]+|\b[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9-]+)*\.(?:com|org|net|io|co|ai|dev|app|uk|hu|de|me)(?:\/[^\s<>]*)?/gi,
    ) || [];
  return [
    ...new Set(
      matches
        .map((value) => {
          try {
            return new URL(
              value.startsWith("http") ? value : `https://${value}`,
            ).hostname.replace(/^www\./, "");
          } catch {
            return "";
          }
        })
        .filter(Boolean),
    ),
  ];
}
export default function AgentContextPanel({
  request,
  onClose,
  scopeKey,
}: {
  request: string;
  onClose: () => void;
  scopeKey?: string;
}) {
  const [profile, setProfile] = useState<Record<string, unknown> | null>(null);
  const [status, setStatus] = useState("loading");
  useEffect(() => {
    let current = true;
    setProfile(null);
    setStatus("loading");
    void getWorkspaceProfile()
      .then((res) => {
        if (current) {
          setProfile(res.profile);
          setStatus("ready");
        }
      })
      .catch(() => {
        if (current) setStatus("error");
      });
    return () => {
      current = false;
    };
  }, [scopeKey]);
  const raw =
    profile?.domain || profile?.website_url || profile?.website || profile?.url;
  const workspace =
    typeof raw === "string" ? requestSites(raw)[0] || raw : null;
  const sites = requestSites(request);
  const mismatch = workspace && sites.some((site) => site !== workspace);
  return (
    <div className={styles.context}>
      <h3>Workspace website</h3>
      {status === "loading" ? (
        <p>Loading workspace profile…</p>
      ) : status === "error" ? (
        <p role="alert">Workspace profile could not be loaded.</p>
      ) : workspace ? (
        <>
          <strong>{workspace}</strong>
          {typeof profile?.business_name === "string" && (
            <p>{profile.business_name}</p>
          )}
        </>
      ) : (
        <p>No workspace website profile is available.</p>
      )}
      <h3>Website mentioned in this request</h3>
      {sites.length ? (
        sites.map((site) => <p key={site}>{site}</p>)
      ) : (
        <p>No website explicitly mentioned.</p>
      )}
      {sites.length > 1 && (
        <p>
          Multiple websites are mentioned. No active website has been selected
          automatically.
        </p>
      )}
      {mismatch && (
        <p className={styles.contextNote}>
          This request mentions {sites.join(", ")}. The workspace profile
          remains {workspace}; it has not been changed.
        </p>
      )}
      <p>
        Remembered information may include details from other websites and can
        still inform this conversation. Review or remove anything that no longer
        applies.
      </p>
      <MemoryPanel
        key={scopeKey}
        isOpen
        onClose={onClose}
        className={styles.memory}
      />
    </div>
  );
}
